import {useFormik, Field, FormikProvider} from 'formik';
import {useTranslation} from 'react-i18next';

import {
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  useTheme,
} from '@mui/material';

import {CompanyProfileDialogButtons} from '../CompanyProfileDialogButtons';
import {PROFILE_SWITCH_SX} from '../profileDialogStyles';

import {EmpionTextField} from '../../../../../components/EmpionTextField/EmpionTextField';
import {RichTextEditor} from '../../../../../components/RichTextEditor/RichTextEditor';
import {getFieldStyle} from '../../../../../layouts/theme/FieldStyles';
import {CompanyProfile} from '../../types';

type CompanyProfileHeaderFormProps = {
  company: CompanyProfile;
  onCancel(): void;
  onSubmit(company: CompanyProfile): void;
};

interface FormValues {
  description_de: string;
  description_en: string;
  toggle: boolean;
  primaryCompanyLanguage: string;
  workMode: string;
  employeeCountRange: string;
  averageEmployeeAge: string;
  companyType: string;
  percOfWomenInLeadership: number;
  percOfEmployeesAreParents: number;
}

export const CompanyProfileGeneralInformationForm = ({company, onCancel, onSubmit}: CompanyProfileHeaderFormProps) => {
  const {t} = useTranslation();
  const theme = useTheme();
  const fieldStyle = getFieldStyle(theme);

  const handleCancel = () => {
    onCancel();
  };

  const validate = (values: {percOfEmployeesAreParents: number; percOfWomenInLeadership: number}) => {
    const errors: Partial<FormValues> = {};

    if (values.percOfEmployeesAreParents > 100 || values.percOfEmployeesAreParents < 0) {
      errors.percOfEmployeesAreParents = 1;
    }
    if (values.percOfWomenInLeadership > 100 || values.percOfWomenInLeadership < 0) {
      errors.percOfWomenInLeadership = 1;
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: {
      description_de: company.description?.de || '',
      description_en: company.description?.en || '',
      toggle: !!company.description?.en || !!company.description?.en,
      primaryCompanyLanguage: company.primaryCompanyLanguage || '',
      workMode: company.workMode || '',
      employeeCountRange: company.employeeCountRange || '',
      averageEmployeeAge: company.averageEmployeeAge || 0,
      companyType: company.companyType || '',
      percOfWomenInLeadership: company.percOfWomenInLeadership || 0,
      percOfEmployeesAreParents: company.percOfEmployeesAreParents || 0,
    },
    validate,
    onSubmit: async (values) => {
      const updatedData: Partial<CompanyProfile> = {
        ...values,
        description: {
          de: values.description_de as any,
          en: values.description_en as any,
        },
      };

      onSubmit({...company, ...updatedData});
    },
  });

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} container justifyContent={'flex-end'}>
            <FormGroup>
              <FormControlLabel
                name='toggle'
                control={<Switch checked={formik.values.toggle} sx={PROFILE_SWITCH_SX} />}
                label={t('shared.switchLabel')}
                onChange={formik.handleChange}
              />
            </FormGroup>
          </Grid>

          {/* description de */}
          <Grid item xs={12} sm={12}>
            <RichTextEditor
              isEditable={true}
              readText={(text: string) => {
                formik.setFieldValue('description_de', text);
              }}
              defaultText={formik.initialValues.description_de}
              fontSize='20px'
            />
          </Grid>

          <Grid container item xs={12} sm={12} spacing={2}>
            {/* Language */}
            <Grid item xs={6} sm={6}>
              <FormControl fullWidth sx={{...fieldStyle}}>
                <InputLabel id='primaryCompanyLanguage'>
                  {t('companyProfile.generalInformation.shared.language')}
                </InputLabel>
                <Select
                  name='primaryCompanyLanguage'
                  id='primaryCompanyLanguage'
                  value={formik.values.primaryCompanyLanguage}
                  label={t('companyProfile.generalInformation.shared.language')}
                  onChange={formik.handleChange}
                >
                  <MenuItem value='en'>{t('companyProfile.generalInformation.dialog.select.language.en')}</MenuItem>
                  <MenuItem value='de'>{t('companyProfile.generalInformation.dialog.select.language.de')}</MenuItem>
                  <MenuItem value='en_de'>
                    {t('companyProfile.generalInformation.dialog.select.language.en_de')}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* work mode */}
            <Grid item xs={6} sm={6}>
              <FormControl fullWidth sx={{...fieldStyle}}>
                <InputLabel id='workMode'>{t('companyProfile.generalInformation.shared.workMode')}</InputLabel>
                <Select
                  name='workMode'
                  id='workMode'
                  value={formik.values.workMode}
                  label={t('companyProfile.generalInformation.shared.workMode')}
                  onChange={formik.handleChange}
                >
                  <MenuItem value='remote'>
                    {t('companyProfile.generalInformation.dialog.select.workMode.remote')}
                  </MenuItem>
                  <MenuItem value='office'>
                    {t('companyProfile.generalInformation.dialog.select.workMode.office')}
                  </MenuItem>
                  <MenuItem value='hybrid'>
                    {t('companyProfile.generalInformation.dialog.select.workMode.hybrid')}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* company size */}
            <Grid item xs={6} sm={6}>
              <FormControl fullWidth sx={{...fieldStyle}}>
                <InputLabel id='employeeCountRange'>{t('companyProfile.generalInformation.shared.size')}</InputLabel>
                <Select
                  name='employeeCountRange'
                  id='employeeCountRange'
                  value={formik.values.employeeCountRange}
                  label={t('companyProfile.generalInformation.shared.size')}
                  onChange={formik.handleChange}
                >
                  <MenuItem value='1 - 9'>1 - 9</MenuItem>
                  <MenuItem value='10 - 49'>10 - 49</MenuItem>
                  <MenuItem value='50 - 249'>50 - 249</MenuItem>
                  <MenuItem value='250'>250+</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* company type */}
            <Grid item xs={6} sm={6}>
              <FormControl fullWidth sx={{...fieldStyle}}>
                <InputLabel id='companyType'>{t('companyProfile.generalInformation.shared.type')}</InputLabel>
                <Select
                  name='companyType'
                  id='companyType'
                  value={formik.values.companyType}
                  label={t('companyProfile.generalInformation.shared.type')}
                  onChange={formik.handleChange}
                >
                  <MenuItem value='startup'>
                    {t('companyProfile.generalInformation.dialog.select.type.startup')}
                  </MenuItem>
                  <MenuItem value='midsize'>
                    {t('companyProfile.generalInformation.dialog.select.type.midsize')}
                  </MenuItem>
                  <MenuItem value='corporate'>
                    {t('companyProfile.generalInformation.dialog.select.type.corporate')}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* % of parents */}
            <Grid item xs={6} sm={6}>
              <Field
                type='number'
                name='percOfEmployeesAreParents'
                label={t('companyProfile.generalInformation.shared.parents')}
                component={EmpionTextField}
                fullWidth
                inputProps={{maxLength: 3}}
                id='percOfEmployeesAreParents'
                multiline
              />
              <FormHelperText>{formik.errors.percOfEmployeesAreParents && '0% - 100%'}</FormHelperText>
            </Grid>

            {/* % women in leadership */}
            <Grid item xs={6} sm={6}>
              <Field
                type='number'
                name='percOfWomenInLeadership'
                id='percOfWomenInLeadership'
                label={t('companyProfile.generalInformation.shared.womenInLeadership')}
                component={EmpionTextField}
                fullWidth
              />
              <FormHelperText>{formik.errors.percOfWomenInLeadership && '0% - 100%'}</FormHelperText>
            </Grid>

            {/* age */}
            <Grid item xs={6} sm={6}>
              <Field
                type='number'
                name='averageEmployeeAge'
                id='averageEmployeeAge'
                label={t('companyProfile.generalInformation.shared.age')}
                component={EmpionTextField}
                fullWidth
              />
            </Grid>

            {/* English input fields */}
            <Grid container>
              {formik.values.toggle && (
                <>
                  <Divider
                    style={{
                      width: '100%',
                      padding: '32px 0 0 16px',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Chip label={t('shared.chipLabel')} variant='outlined' />
                  </Divider>
                  <Grid container spacing={2} mt={2} pl={2}>
                    <Grid item xs={12}>
                      <RichTextEditor
                        isEditable={true}
                        readText={(text: string) => {
                          formik.setFieldValue('description_en', text);
                        }}
                        defaultText={formik.initialValues.description_en}
                        fontSize='20px'
                      />
                    </Grid>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>

          {/* Buttons */}
          <Grid item container justifyContent={'flex-end'} display={'flex'}>
            <CompanyProfileDialogButtons onCancel={handleCancel} />
          </Grid>
        </Grid>
      </form>
    </FormikProvider>
  );
};
